import React, { useState, useEffect } from 'react';
import { Feature, Component, TestCase, PlatformItem } from '../../types';
import { MessageSetter } from '../../utils/messages';
import { Icons } from '../../utils/icons';
import TestCaseCard from './TestCaseCard';
import BulkEditComponentsModal from './BulkEditComponentsModal';
import ChangeHistoryModal from './ChangeHistoryModal';
import { apiRequest } from '../../utils/api';

interface UserInfo {
  id: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string;
}

interface ChangeHistoryItem {
  type: 'Feature' | 'Component' | 'Test Case';
  name: string;
  id: string;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
}

interface FeatureViewModalProps {
  feature: Feature;
  components: Component[];
  testCases: TestCase[];
  dragOverComponent: string | null;
  draggedTestCase: string | null;
  onClose: () => void;
  onDelete: (featureId: string) => Promise<void>;
  onEdit?: (feature: Feature) => void;
  onCreateComponent: (featureId: string) => Promise<void>;
  onEditComponent: (component: Component) => Promise<void>;
  onDeleteComponent: (componentId: string) => Promise<void>;
  onUpdateComponent: (componentId: string, updates: Partial<Component>) => Promise<void>;
  onCreateTestCase: (componentIdOrFeatureId: string, isFeature?: boolean) => Promise<void>;
  onDeleteTestCase: (testCaseId: string) => void;
  onUpdateTestCase: (testCaseId: string, updates: Partial<TestCase>) => Promise<void>;
  onTestCaseDragStart: (e: React.DragEvent, testCaseId: string) => void;
  onTestCaseDragEnd: (e: React.DragEvent) => void;
  onComponentDragOver: (e: React.DragEvent, componentId: string) => void;
  onComponentDragLeave: () => void;
  onTestCaseDrop: (e: React.DragEvent, componentId: string) => Promise<void>;
  onEditTestCase?: (testCaseId: string) => void;
  onViewTestCase?: (testCase: TestCase) => void;
  setMessage?: MessageSetter;
  platforms?: PlatformItem[];
  productId?: string;
  projectCode?: string; // Project code prefix for TC ID display (e.g., "FS" -> "FS-TC-42")
  onComponentsUpdated?: () => Promise<void>;
}

interface ComponentPlatformEditorProps {
  component: Component;
}

const ComponentPlatformEditor: React.FC<ComponentPlatformEditorProps> = ({
  component
}) => {
  return (
    <div className="mt-xs" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {/* Platforms */}
      {component.platforms && component.platforms.length > 0 ? (
        <div className="flex gap-xs flex-wrap items-center">
          <span className="label text-xs" style={{ marginRight: '0.5rem', color: 'var(--text-secondary)' }}>PLATFORMS:</span>
          {component.platforms.map(platform => (
            <span key={platform} className="badge badge-cyan" style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}>
              {platform.toUpperCase()}
            </span>
          ))}
        </div>
      ) : (
        <span className="hint text-xs">No platforms assigned</span>
      )}
      
      {/* Milestone */}
      {component.milestone && (
        <div className="flex gap-xs flex-wrap items-center">
          <span className="label text-xs" style={{ marginRight: '0.5rem', color: 'var(--text-secondary)' }}>MILESTONE:</span>
          <span className="badge badge-cyan" style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}>
            {component.milestone}
          </span>
        </div>
      )}
    </div>
  );
};

const FeatureViewModal: React.FC<FeatureViewModalProps> = ({
  feature,
  components,
  testCases,
  dragOverComponent,
  draggedTestCase,
  onClose,
  onDelete,
  onEdit,
  onCreateComponent,
  onEditComponent,
  onDeleteComponent,
  onUpdateComponent,
  onCreateTestCase,
  onDeleteTestCase,
  onUpdateTestCase,
  onTestCaseDragStart,
  onTestCaseDragEnd,
  onComponentDragOver,
  onComponentDragLeave,
  onTestCaseDrop,
  onEditTestCase,
  onViewTestCase,
  setMessage,
  platforms = [],
  productId,
  projectCode,
  onComponentsUpdated
}) => {
  const featureComponents = components.filter(c => c.featureId === feature.id);
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(null);
  const [selectedComponentIds, setSelectedComponentIds] = useState<Set<string>>(new Set());
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [changeHistoryItem, setChangeHistoryItem] = useState<ChangeHistoryItem | null>(null);
  const [users, setUsers] = useState<Record<string, UserInfo>>({});

  // Fetch users for display names
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const userList = await apiRequest<UserInfo[]>('/api/users/for-sessions', {}, { useAuth: true });
        const userMap: Record<string, UserInfo> = {};
        userList.forEach(user => {
          userMap[user.id] = user;
        });
        setUsers(userMap);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchUsers();
  }, []);

  const getUserName = (userId?: string): string => {
    if (!userId) return '';
    const user = users[userId];
    if (user) {
      return user.displayName;
    }
    return '';
  };
  
  // Auto-select first component if available, or update selection if current is deleted
  useEffect(() => {
    if (featureComponents.length > 0) {
      if (!selectedComponentId || !featureComponents.find(c => c.id === selectedComponentId)) {
        setSelectedComponentId(featureComponents[0].id);
      }
    } else {
      setSelectedComponentId(null);
    }
  }, [featureComponents, selectedComponentId]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);
  
  const getTestCasesForComponent = (componentId: string): TestCase[] => {
    return testCases.filter(tc => tc.componentId === componentId);
  };

  // Get test cases at feature level (no component)
  const getTestCasesForFeature = (): TestCase[] => {
    return testCases.filter(tc => !tc.componentId && tc.featureId === feature.id);
  };

  // Count total test cases for this feature (component-level + feature-level)
  const getTotalTestCasesCount = (): number => {
    const componentTestCases = featureComponents.reduce((sum, comp) => {
      return sum + getTestCasesForComponent(comp.id).length;
    }, 0);
    const featureLevelTestCases = getTestCasesForFeature().length;
    return componentTestCases + featureLevelTestCases;
  };

  const selectedComponent = selectedComponentId 
    ? featureComponents.find(c => c.id === selectedComponentId)
    : null;
  
  const selectedComponentTestCases = selectedComponentId
    ? getTestCasesForComponent(selectedComponentId)
    : [];
  
  const isDragOver = selectedComponentId && dragOverComponent === selectedComponentId;

  return (
    <div className="modal-overlay">
      <div className="card modal-content feature-view-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '95vw', width: '1200px', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h2 className="title-lg" style={{ marginBottom: '0.5rem' }}>{feature.name}</h2>
            {feature.description && (
              <p className="subtitle" style={{ marginBottom: '0.5rem' }}>{feature.description}</p>
            )}
            <div className="flex gap-xs items-center flex-wrap">
              <span className="subdep-count-badge">
                {featureComponents.length} component{featureComponents.length !== 1 ? 's' : ''}
              </span>
              {getTotalTestCasesCount() > 0 && (
                <span className="subdep-count-badge">
                  {getTotalTestCasesCount()} test case{getTotalTestCasesCount() !== 1 ? 's' : ''}
                </span>
              )}
              <button
                className="btn btn-ghost"
                onClick={() => setChangeHistoryItem({
                  type: 'Feature',
                  name: feature.name,
                  id: feature.id,
                  createdBy: feature.createdBy,
                  createdAt: feature.createdAt,
                  updatedBy: feature.updatedBy,
                  updatedAt: feature.updatedAt
                })}
                style={{ 
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.75rem',
                  color: 'var(--text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
                title="View change history"
              >
                <span>📝</span>
                {feature.updatedBy && getUserName(feature.updatedBy) ? (
                  <span>Updated by {getUserName(feature.updatedBy)}</span>
                ) : feature.createdBy && getUserName(feature.createdBy) ? (
                  <span>Created by {getUserName(feature.createdBy)}</span>
                ) : (
                  <span>View history</span>
                )}
              </button>
            </div>
          </div>
          <div className="flex gap-sm items-center">
            {onEdit && (
              <button
                className="btn btn-secondary btn-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(feature);
                }}
                style={{
                  padding: '0.5rem',
                  minWidth: '2.25rem',
                  minHeight: '2.25rem',
                  width: '2.25rem',
                  height: '2.25rem',
                  fontSize: '1rem',
                  lineHeight: '1'
                }}
                title="Edit"
              >
                ✏️
              </button>
            )}
            <button
              className="btn btn-secondary btn-icon"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(feature.id);
              }}
              style={{
                padding: '0.5rem',
                minWidth: '2.25rem',
                minHeight: '2.25rem',
                width: '2.25rem',
                height: '2.25rem',
                fontSize: '1rem',
                color: 'var(--danger)',
                lineHeight: '1'
              }}
              title="Delete"
            >
              {Icons.delete}
            </button>
            <button
              className="btn btn-ghost btn-icon"
              onClick={onClose}
              style={{ fontSize: '1.5rem' }}
            >
              ×
            </button>
          </div>
        </div>

        <div className="feature-view-split" style={{ paddingTop: '1rem', borderTop: '1px solid var(--grid-line)' }}>
          {/* Feature-level test cases section */}
          {getTestCasesForFeature().length > 0 && (
            <div className="mb-md">
              <div className="flex justify-between items-center mb-sm">
                <h4 className="subdeps-section-title">Feature Test Cases</h4>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => onCreateTestCase(feature.id, true)}
                >
                  + Add Test Case
                </button>
              </div>
              <div className="testcases-grid">
                {getTestCasesForFeature().map(testCase => {
                  const isDragging = draggedTestCase === testCase.id;
                  return (
                    <TestCaseCard
                      key={testCase.id}
                      testCase={testCase}
                      isDragging={isDragging}
                      onDelete={onDeleteTestCase}
                      onDragStart={onTestCaseDragStart}
                      onDragEnd={onTestCaseDragEnd}
                      onEdit={onEditTestCase}
                      onView={onViewTestCase}
                      setMessage={setMessage}
                      onShowChangeHistory={(tc) => setChangeHistoryItem({
                        type: 'Test Case',
                        name: tc.title,
                        id: tc.id,
                        createdBy: tc.createdBy,
                        createdAt: tc.createdAt,
                        updatedBy: tc.updatedBy,
                        updatedAt: tc.updatedAt
                      })}
                      getUserName={getUserName}
                      projectCode={projectCode}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Components Section - Split View */}
          <div className="mb-md" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4 className="subdeps-section-title">Components</h4>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {bulkEditMode && (
                <>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      // Select all components
                      const allComponentIds = new Set(featureComponents.map(c => c.id));
                      setSelectedComponentIds(allComponentIds);
                    }}
                    title="Select all components"
                  >
                    Select All
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setBulkEditMode(false);
                      setSelectedComponentIds(new Set());
                    }}
                  >
                    Cancel Selection
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      if (selectedComponentIds.size > 0) {
                        setShowBulkEditModal(true);
                      }
                    }}
                    disabled={selectedComponentIds.size === 0}
                  >
                    Bulk Edit ({selectedComponentIds.size})
                  </button>
                </>
              )}
              {!bulkEditMode && featureComponents.length > 0 && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setBulkEditMode(true)}
                >
                  Select Multiple
                </button>
              )}
            </div>
          </div>

          {featureComponents.length === 0 ? (
            <div className="feature-view-split-container">
              <div className="component-nav-sidebar">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => onCreateComponent(feature.id)}
                  style={{ width: '100%' }}
                >
                  + Add Component
                </button>
              </div>
              <div className="component-details-panel">
                <div className="component-details-empty">
                  <p className="text-muted">No components yet. Add one to get started.</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="feature-view-split-container">
              {/* Left Sidebar - Component Navigation */}
              <div className="component-nav-sidebar">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => onCreateComponent(feature.id)}
                  style={{ width: '100%', marginBottom: '0.75rem' }}
                >
                  + Add Component
                </button>
                {featureComponents.map(component => {
                  const componentTestCases = getTestCasesForComponent(component.id);
                  const isSelected = selectedComponentId === component.id;
                  const isBulkSelected = bulkEditMode && selectedComponentIds.has(component.id);
                  const isDragOver = dragOverComponent === component.id;
                  return (
                    <div
                      key={component.id}
                      className={`component-nav-item ${isSelected ? 'active' : ''} ${isDragOver ? 'drag-over' : ''} ${isBulkSelected ? 'active' : ''}`}
                      onClick={(e) => {
                        if (bulkEditMode) {
                          e.stopPropagation();
                          const newSelection = new Set(selectedComponentIds);
                          if (isBulkSelected) {
                            newSelection.delete(component.id);
                          } else {
                            newSelection.add(component.id);
                          }
                          setSelectedComponentIds(newSelection);
                        } else {
                          setSelectedComponentId(component.id);
                        }
                      }}
                      onDragOver={(e) => {
                        if (!bulkEditMode) {
                          e.preventDefault();
                          e.stopPropagation();
                          onComponentDragOver(e, component.id);
                        }
                      }}
                      onDragLeave={onComponentDragLeave}
                      onDrop={(e) => {
                        if (!bulkEditMode) {
                          e.preventDefault();
                          e.stopPropagation();
                          onTestCaseDrop(e, component.id);
                        }
                      }}
                      style={{
                        cursor: 'pointer',
                        border: isDragOver ? '2px solid var(--cyan-glow)' : undefined,
                        backgroundColor: isDragOver ? 'var(--surface-elevated)' : (isBulkSelected ? 'var(--cyan-transparent)' : undefined),
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      {bulkEditMode && (
                        <input
                          type="checkbox"
                          checked={isBulkSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            const newSelection = new Set(selectedComponentIds);
                            if (e.target.checked) {
                              newSelection.add(component.id);
                            } else {
                              newSelection.delete(component.id);
                            }
                            setSelectedComponentIds(newSelection);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{ 
                            cursor: 'pointer', 
                            flexShrink: 0,
                            width: '1rem',
                            height: '1rem',
                            marginRight: '0.5rem'
                          }}
                        />
                      )}
                      <div className="component-nav-name" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {component.name}
                      </div>
                      <div className="component-nav-meta" style={{ flexShrink: 0 }}>
                        {componentTestCases.length > 0 && (
                          <span className="badge badge-cyan" style={{ fontSize: '0.65rem' }}>
                            {componentTestCases.length}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Right Panel - Component Details & Test Cases */}
              <div 
                className={`component-details-panel ${isDragOver ? 'drag-over' : ''}`}
                onDragOver={selectedComponentId ? (e) => onComponentDragOver(e, selectedComponentId) : undefined}
                onDragLeave={onComponentDragLeave}
                onDrop={selectedComponentId ? (e) => onTestCaseDrop(e, selectedComponentId) : undefined}
              >
                {selectedComponent ? (
                  <>
                    {/* Component Header */}
                    <div className="component-details-header">
                      <div className="flex-1">
                        <h3 className="title-md" style={{ marginBottom: '0.5rem' }}>
                          {selectedComponent.name}
                        </h3>
                        <ComponentPlatformEditor component={selectedComponent} />
                        {selectedComponent.description && (
                          <p className="subtitle mt-sm">{selectedComponent.description}</p>
                        )}
                        {/* Change History Link */}
                        <button
                          className="btn btn-ghost"
                          onClick={() => setChangeHistoryItem({
                            type: 'Component',
                            name: selectedComponent.name,
                            id: selectedComponent.id,
                            createdBy: selectedComponent.createdBy,
                            createdAt: selectedComponent.createdAt,
                            updatedBy: selectedComponent.updatedBy,
                            updatedAt: selectedComponent.updatedAt
                          })}
                          style={{ 
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.75rem',
                            color: 'var(--text-secondary)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            marginTop: '0.5rem'
                          }}
                          title="View change history"
                        >
                          <span>📝</span>
                          {selectedComponent.updatedBy && getUserName(selectedComponent.updatedBy) ? (
                            <span>Updated by {getUserName(selectedComponent.updatedBy)}</span>
                          ) : selectedComponent.createdBy && getUserName(selectedComponent.createdBy) ? (
                            <span>Created by {getUserName(selectedComponent.createdBy)}</span>
                          ) : (
                            <span>View history</span>
                          )}
                        </button>
                      </div>
                      <div className="flex gap-xs items-center">
                        <button
                          className="btn btn-secondary btn-icon"
                          onClick={() => onEditComponent(selectedComponent)}
                          style={{
                            padding: '0.5rem',
                            minWidth: '2.25rem',
                            minHeight: '2.25rem',
                            width: '2.25rem',
                            height: '2.25rem',
                            fontSize: '1rem',
                            lineHeight: '1'
                          }}
                          title="Edit component"
                        >
                          ✏️
                        </button>
                        <button
                          className="btn btn-secondary btn-icon"
                          onClick={async () => {
                            await onDeleteComponent(selectedComponent.id);
                            // Select first component or null if none left
                            const remaining = featureComponents.filter(c => c.id !== selectedComponent.id);
                            setSelectedComponentId(remaining.length > 0 ? remaining[0].id : null);
                          }}
                          style={{
                            padding: '0.5rem',
                            minWidth: '2.25rem',
                            minHeight: '2.25rem',
                            width: '2.25rem',
                            height: '2.25rem',
                            fontSize: '1rem',
                            color: 'var(--danger)',
                            lineHeight: '1'
                          }}
                          title="Delete component"
                        >
                          {Icons.delete}
                        </button>
                      </div>
                    </div>

                    {/* Test Cases Section */}
                    <div className="component-test-cases-section">
                      <div className="flex justify-between items-center mb-sm">
                        <h4 className="subdeps-section-title">Test Cases</h4>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => onCreateTestCase(selectedComponent.id)}
                        >
                          + Add Test Case
                        </button>
                      </div>

                      {selectedComponentTestCases.length === 0 ? (
                        <div className={`test-case-empty-state ${isDragOver ? 'drop-zone active' : ''}`}>
                          <p className="hint">
                            {isDragOver ? 'Drop test case here' : 'No test cases yet. Add one to get started.'}
                          </p>
                        </div>
                      ) : (
                        <div className="testcases-grid">
                          {selectedComponentTestCases.map(testCase => {
                            const isDragging = draggedTestCase === testCase.id;
                            return (
                              <TestCaseCard
                                key={testCase.id}
                                testCase={testCase}
                                isDragging={isDragging}
                                onDelete={onDeleteTestCase}
                                onDragStart={onTestCaseDragStart}
                                onDragEnd={onTestCaseDragEnd}
                                onEdit={onEditTestCase}
                                onView={onViewTestCase}
                                setMessage={setMessage}
                                onShowChangeHistory={(tc) => setChangeHistoryItem({
                                  type: 'Test Case',
                                  name: tc.title,
                                  id: tc.id,
                                  createdBy: tc.createdBy,
                                  createdAt: tc.createdAt,
                                  updatedBy: tc.updatedBy,
                                  updatedAt: tc.updatedAt
                                })}
                                getUserName={getUserName}
                                projectCode={projectCode}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="component-details-empty">
                    <p className="text-muted">Select a component to view its details and test cases</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bulk Edit Modal */}
      {showBulkEditModal && (
        <BulkEditComponentsModal
          isOpen={showBulkEditModal}
          onClose={() => {
            setShowBulkEditModal(false);
            setBulkEditMode(false);
            setSelectedComponentIds(new Set());
          }}
          selectedComponents={featureComponents.filter(c => selectedComponentIds.has(c.id))}
          allPlatforms={platforms}
          productId={productId}
          onSuccess={async () => {
            if (onComponentsUpdated) {
              await onComponentsUpdated();
            }
            setBulkEditMode(false);
            setSelectedComponentIds(new Set());
          }}
          setMessage={setMessage || (() => {})}
        />
      )}

      {/* Change History Modal */}
      {changeHistoryItem && (
        <ChangeHistoryModal
          item={changeHistoryItem}
          onClose={() => setChangeHistoryItem(null)}
        />
      )}
    </div>
  );
};

export default FeatureViewModal;

